import React from 'react'
import papa from 'papaparse'
import { useHistory } from 'react-router-dom'
import JSZip from 'jszip'
import FileSaver from 'file-saver'
import { pdf } from '@react-pdf/renderer'
import { formatISO, set, getHours, getMinutes, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import {
  TestSessionDetailWithResult_testSession_Location,
  Lab,
  useAuth,
  UserRole,
  getFormattedAddress,
  getFormattedDateAndTime,
  TestType,
  useAddressForm,
  AddressFormValues,
  useSetTestSessionLab,
  useSetAntigenTypeTestSession,
  TestSessionDetailWithResult,
  TestSessionDetailWithResultVariables,
  TestSessionWithoutMembersWithResult_testSession,
  TEST_SESSION_DETAIL,
  UserPermissions,
  SpecimenTypes,
  useSendJapaneseEmail,
  Gender,
  TestSessionDetailWithResult_testSession_Groups,
  TestSessionDetailWithResult_testSession_Tests_Members,
  RequestTestResultPdfUrl,
  RequestTestResultPdfUrlVariables,
  CategoryTable,
  TestTypeOptionsLabels,
  TestValuesInput,
} from '@modmd/data'
import { COLOR, DEVICE, fontSizes } from 'theme'
import { flatMap } from 'lodash'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import Card from 'components/Card'
import { Text } from 'components/Typography'
import { Box } from 'components/Layout'
import { Dialog, ConfirmationDialog } from 'components/Dialog'
import { AddressInput } from 'components/AddressInput'
import Icon from 'components/Icon'
import { ROUTES } from 'internal-portal/constants/routes'
import { DateInput } from 'components/DateInput'
import { TestSessionStatusLabel } from 'sharedComponents/TestSessionStatusLabel'
import { TestSessionPaymentLabel } from 'sharedComponents/TestSessionPaymentLabel'
import {
  TestSessionPaymentError,
  TestSessionPaymentErrorLabel,
} from 'sharedComponents/TestSessionPaymentErrorLabel'
import { SelectInput } from 'components/SelectInput'
import { useToaster } from 'utils/useToaster'
import { Loading } from 'components/Loading'
import { OrderedProductsInfo } from 'sharedComponents/OrderedProductsInfo'
import PDFDocument, {
  PDFDocumentProps,
} from 'sharedComponents/DownloadTestResultPdfButton/PDFDocument'
import PDFJapanDocument, {
  PDFCertificateDocumentProps,
} from 'sharedComponents/DownloadTestResultPdfButton/PDFJapanDocument'
import { useLazyQuery, useApolloClient } from '@apollo/client'
import { REQUEST_TEST_RESULT_PDF_URL } from 'sharedComponents/DownloadTestResultPdfButton/operations'
import { Sublocation } from 'sharedComponents/Sublocation/Sublocation'
import { isInfluenza, setTimeToDate } from 'utils/helpers'
import { OptionType } from 'components/SelectInput/SelectInput'
import { SessionName } from './SessionName'
import { LAB_OPTIONS } from '../TestSessions/utils'
import { DefaultValuesDialog } from '../EventsList/DefaultValuesDialog'

const NO_LAB_VALUE = ''

interface Props {
  session: TestSessionWithoutMembersWithResult_testSession
  onChange: (sessionChanges: Partial<TestSessionWithoutMembersWithResult_testSession>) => void
  isEditMode: boolean
  testSessionName: string
  refetchTestSessionData: () => Promise<unknown>
  isEditing?: boolean
  isEditLoading?: boolean
  isSuperAdminOrResultsOrLiaison?: boolean
}

interface CsvProps {
  barcode: string
  birthDate?: string | Date | null
  firstName?: string
  lastName?: string
  email?: string
  gender?: Gender | null | undefined
  phoneNumber?: string
  id?: string
  lab?: Lab | null | undefined
  result?: string
  resultReceivedAt?: string | Date | null
  specimenType?: SpecimenTypes
  testedAt?: string | Date | null
  type?: TestType | undefined
  Groups?: TestSessionDetailWithResult_testSession_Groups[]
  Address?: {
    city?: string
    state?: string
    street?: string
    zip?: string
  }
}

enum EditDialogType {
  Date = 'Date',
  Location = 'Location',
}

type DownloadType = 'csvData' | 'csvpatients' | 'csv-jp' | 'send-japanese-email' | null

const SessionInfo = ({
  session,
  isEditMode,
  onChange,
  testSessionName,
  refetchTestSessionData,
  isEditing,
  isEditLoading,
  isSuperAdminOrResultsOrLiaison,
}: Props) => {
  const apolloClient = useApolloClient()
  const isMobileOrTablet = !useIsMinDevice(DEVICE.DESKTOP)
  const { hasRoles, hasPermission } = useAuth()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])
  const isCLS = hasRoles([UserRole.CLS])
  const isScheduler = hasRoles([UserRole.SCHEDULER])
  const history = useHistory()
  const [openedEditDialogType, setOpenedEditDialogType] = React.useState<EditDialogType | null>(
    null
  )
  const [date, setDate] = React.useState<Date | null>(parseISO(session.date))
  const [isOpenConfirmModal, setIsOpenConfirmModal] = React.useState<boolean>(false)
  const [endDate, setEndDate] = React.useState<Date | null>(
    session.endsAt ? parseISO(session.endsAt) : null
  )
  const [download, setDownload] = React.useState<DownloadType>(null)
  const formattedDateTime = date ? getFormattedDateAndTime(date, true) : undefined
  const formattedEndDateTime = endDate
    ? ` - ${getFormattedDateAndTime(endDate, true, false)}`
    : undefined
  const testSessionId = session.id
  const [sendJapaneseEmail] = useSendJapaneseEmail()
  const { setToastMessage } = useToaster()
  const [isDefaultValuesOpen, setIsDefaultValuesOpen] = React.useState(false)

  const getAntigenByCategory = React.useMemo<OptionType[]>((): OptionType[] => {
    const antigenOptions: TestType[] = [
      TestType.Antigen,
      TestType.SiennaAntigen,
      TestType.IndicaidAntigen,
      TestType.AcceavaStrep,
      TestType.SofiaStrep,
      TestType.SofiaInfluenza,
      TestType.BioSignFlu,
    ]

    return antigenOptions.includes(session.type)
      ? antigenOptions.reduce((acc, antigen) => {
          if (
            CategoryTable[antigen as keyof typeof CategoryTable] ===
            (session.category as keyof typeof CategoryTable)
          ) {
            acc.push({
              value: antigen,
              label: TestTypeOptionsLabels[antigen as keyof typeof TestTypeOptionsLabels],
            })
          }

          return acc
        }, [] as OptionType[])
      : []
  }, [session.type, session.category])

  const createPDF = async (pdfContent: PDFDocumentProps) => {
    if (pdfContent.result) {
      if (
        pdfContent.lab === Lab.ModmdLab ||
        pdfContent.lab === Lab.ModmdAtl ||
        pdfContent.lab === Lab.DendiLax ||
        pdfContent.lab === Lab.DendiAtl
      ) {
        let blob
        let retryAttempts = 0
        do {
          try {
            // eslint-disable-next-line no-await-in-loop
            blob = await pdf(<PDFDocument data={pdfContent} />).toBlob()
          } catch (error) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 500))
            retryAttempts += 1
          }
        } while (!blob || retryAttempts > 3)

        return blob
      }
      const { data } = await apolloClient.query<
        RequestTestResultPdfUrl,
        RequestTestResultPdfUrlVariables
      >({
        query: REQUEST_TEST_RESULT_PDF_URL,
        variables: { id: pdfContent.id },
      })
      if (data.requestTestResultPdfUrl) {
        return fetch(data.requestTestResultPdfUrl).then((r) => {
          if (r.status === 200) return r.blob()
          return undefined
        })
      }
    }

    return null
  }

  const createCertificatePDF = async (pdfContent: PDFDocumentProps) => {
    if (pdfContent.result) {
      if (
        pdfContent.lab === Lab.ModmdLab ||
        pdfContent.lab === Lab.ModmdAtl ||
        pdfContent.lab === Lab.DendiLax ||
        pdfContent.lab === Lab.DendiAtl ||
        pdfContent.lab === Lab.Pathline
      ) {
        let blob
        let retryAttempts = 0
        do {
          try {
            // eslint-disable-next-line no-await-in-loop
            blob = await pdf(
              <PDFJapanDocument data={pdfContent as PDFCertificateDocumentProps} />
            ).toBlob()
          } catch (error) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 500))
            retryAttempts += 1
          }
        } while (!blob || retryAttempts > 3)

        return blob
      }
      const { data } = await apolloClient.query<
        RequestTestResultPdfUrl,
        RequestTestResultPdfUrlVariables
      >({
        query: REQUEST_TEST_RESULT_PDF_URL,
        variables: { id: pdfContent.id },
      })
      if (data.requestTestResultPdfUrl) {
        return fetch(data.requestTestResultPdfUrl).then((r) => {
          if (r.status === 200) return r.blob()
          return undefined
        })
      }
    }

    return null
  }
  const handleConfirmModal = () => {
    setIsOpenConfirmModal(!isOpenConfirmModal)
  }

  const getCsvResult = (
    type: TestType | undefined,
    result: string | null | undefined,
    secondResult: string | null | undefined
  ) => {
    if (!result) return ''
    if (!isInfluenza(type)) return result
    return `A: ${result ?? ''} | B: ${secondResult ?? ''}`
  }

  const [getAllMembers, { loading: TestSessionAllMembersLoading }] = useLazyQuery<
    TestSessionDetailWithResult,
    TestSessionDetailWithResultVariables
  >(TEST_SESSION_DETAIL, {
    onCompleted: async (data) => {
      const pdfData = [] as Array<CsvProps>
      const patients = data.testSession?.Members?.filter(({ isParticipant }) => isParticipant) || []
      if (download === 'csvData') {
        const testedMembers = flatMap(data?.testSession?.Tests, ({ Members }) =>
          Members.filter(({ isParticipant }) => isParticipant)
        )
        const csvData = testedMembers.map(({ Group, User: { firstName, lastName, id } }) => ({
          'last name': lastName ?? '',
          'first name': firstName ?? '',
          result: getCsvResult(
            data?.testSession?.type,
            data?.testSession?.Tests.find(({ Members }) =>
              Members.some((Member) => Member.User.id === id)
            )?.result,
            data?.testSession?.Tests.find(({ Members }) =>
              Members.some((Member) => Member.User.id === id)
            )?.secondResult
          ),
          'company name': data?.testSession?.Company?.name ?? '',
          'group name': Group?.name ?? '',
        }))
        const csv = papa.unparse(csvData, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Test-Session-${testSessionName}.csv`)
        document.body.appendChild(link)
        link.click()
      }
      if (download === 'csvpatients') {
        const csvpatients = patients
          .filter(
            ({ User: { isCompliant, firstName, lastName } }) => isCompliant && firstName && lastName
          )
          .map(({ User: { firstName, lastName, birthDate, email } }) => {
            const dateOfBirth = birthDate ? getFormattedDateAndTime(birthDate) : ''
            return {
              Patients: `${firstName || ''} ${lastName || ''} ${
                dateOfBirth && `, ${dateOfBirth}`
              }, ${email || ''}`,
            }
          })
        const csv = papa.unparse(csvpatients, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Patients-of-${testSessionName}.csv`)
        document.body.appendChild(link)
        link.click()
      }
      if (download === 'csv-jp') {
        const getCollectionTime = (testedAt: string) =>
          data.testSession?.lab === Lab.Pathline ||
          data.testSession?.lab === Lab.ModmdAtl ||
          data.testSession?.lab === Lab.DendiLax ||
          data.testSession?.lab === Lab.DendiAtl
            ? `${formatInTimeZone(testedAt, 'America/New_York', 'hh:mm aa')} EST`
            : `${formatInTimeZone(testedAt, 'America/Los_Angeles', 'hh:mm aa')} PST`

        const csvpatients = data.testSession?.Tests.map(
          ({ id, barcode, result, testedAt, specimenType, Members }) => {
            const member =
              Members?.find(({ isParticipant }) => isParticipant) ||
              ({} as TestSessionDetailWithResult_testSession_Tests_Members)
            const patient = member?.User
            if (!patient) return {}

            const remainingData = patients?.find(({ User }) => User.id === patient.id)

            pdfData.push({
              barcode: barcode || '-',
              birthDate: remainingData?.User.birthDate,
              firstName: patient.firstName || '-',
              lastName: patient.lastName || '-',
              email: remainingData?.User.email || '-',
              gender: remainingData?.User.gender,
              phoneNumber: remainingData?.User.phoneNumber || '-',
              id,
              lab: data?.testSession?.lab,
              result: result || '',
              resultReceivedAt: testedAt,
              specimenType: specimenType || SpecimenTypes.midNasal,
              testedAt,
              type: data?.testSession?.type,
              Address: {
                city: remainingData?.User?.Address?.city || '',
                state: remainingData?.User?.Address?.state || '',
                street: remainingData?.User?.Address?.street || '',
                zip: remainingData?.User?.Address?.zip || '',
              },
            })
            return {
              'Last name': patient.lastName,
              'Fist name': patient.firstName,
              result,
              'company name': data.testSession?.Company?.name,
              'group name': member?.Group?.name,
              'collection time': testedAt ? getCollectionTime(testedAt) : '',
              'specimen type': specimenType,
              'result file name': `${patient.firstName?.substring(0, 4) || ''}_${
                patient.lastName?.substring(0, 4) || ''
              }_${barcode || ''}.pdf`,
              'CTO file name': `${patient.firstName?.substring(0, 4) || ''}_${
                patient.lastName?.substring(0, 4) || ''
              }_${getFormattedDateAndTime(
                remainingData?.User.birthDate,
                false,
                true,
                'MMddyyyy'
              )}.pdf`,
              dob: remainingData?.User.birthDate
                ? getFormattedDateAndTime(remainingData.User.birthDate)
                : '',
            }
          }
        )
        if (csvpatients) {
          const csv = papa.unparse(csvpatients, { header: true })
          const dataBlob = new Blob([csv], { type: 'text/csv' })

          const [firstContent, ...remainingContents] = pdfData
          const firstBlob = await createPDF(firstContent as PDFDocumentProps)
          const remainingBlobs = await Promise.all(
            remainingContents.map((pdfContent) => createPDF(pdfContent as PDFDocumentProps))
          )

          const firstCertificateJapan = await createCertificatePDF(firstContent as PDFDocumentProps)
          const remainingCertificateJapan = await Promise.all(
            remainingContents.map((pdfContent) =>
              createCertificatePDF(pdfContent as PDFDocumentProps)
            )
          )

          const allBlobs = [firstBlob, ...remainingBlobs]

          const allBlobsJapan = [firstCertificateJapan, ...remainingCertificateJapan]

          const zip = new JSZip()
          zip.file('Japan.csv', dataBlob)
          allBlobs?.map((e, index) => {
            if (e)
              return zip.file(
                `/results/${pdfData[index].firstName?.substring(0, 4) || ''}_${
                  pdfData[index].lastName?.substring(0, 4) || ''
                }_${pdfData[index].barcode}.pdf`,
                e
              )
            return undefined
          })

          allBlobsJapan?.map((e, index) => {
            if (e)
              return zip.file(
                `/COT/${pdfData[index].firstName?.substring(0, 4) || ''}_${
                  pdfData[index].lastName?.substring(0, 4) || ''
                }_${getFormattedDateAndTime(
                  pdfData[index].birthDate || '',
                  false,
                  true,
                  'MMddyyyy'
                )}.pdf`,
                e
              )
            return undefined
          })

          void zip.generateAsync({ type: 'blob' }).then(function gen(content) {
            FileSaver.saveAs(content, 'download.zip')
          })
        }
      }

      const blobToBase64 = (blob: Blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
        })

      if (download === 'send-japanese-email') {
        const resultedTests = data.testSession?.Tests.filter((test) => test.result) || []
        resultedTests?.map(async ({ id, barcode, result, testedAt, specimenType, Members }) => {
          const member =
            Members?.find(({ isParticipant }) => isParticipant) ||
            ({} as TestSessionDetailWithResult_testSession_Tests_Members)
          const patient = member?.User
          if (!patient) return {}
          const remainingData = patients?.find(({ User }) => User.id === patient.id)
          const PDF_DATA = {
            barcode: barcode || '-',
            birthDate: remainingData?.User.birthDate,
            firstName: patient.firstName || '-',
            lastName: patient.lastName || '-',
            email: remainingData?.User.email || '-',
            gender: remainingData?.User.gender || '',
            phoneNumber: remainingData?.User.phoneNumber || '-',
            id,
            lab: data?.testSession?.lab || '',
            result: result || '',
            resultReceivedAt: testedAt,
            specimenType: specimenType || SpecimenTypes.midNasal,
            testedAt,
            type: data?.testSession?.type || '',
            Address: {
              city: remainingData?.User?.Address?.city || '',
              state: remainingData?.User?.Address?.state || '',
              street: remainingData?.User?.Address?.street || '',
              zip: remainingData?.User?.Address?.zip || '',
            },
          }
          const pdfBlobResult = await createPDF(PDF_DATA)

          const remaining = [PDF_DATA]

          const pdfBlobCTO = await Promise.all(
            remaining.map((pdfContent) => createCertificatePDF(pdfContent))
          )

          const pdfbase64 = pdfBlobResult && ((await blobToBase64(pdfBlobResult)) as string)
          const pdfResult = {
            base64: pdfbase64?.replace('data:application/pdf;base64,', '') || '',
            fileName: `${patient.firstName || ''}_${patient.lastName || ''}.pdf`,
          }

          const pdfCTObase64 = pdfBlobCTO && ((await blobToBase64(pdfBlobCTO[0] as Blob)) as string)
          const pdfCTOResult = {
            base64: pdfCTObase64?.replace('data:application/pdf;base64,', '') || '',
            fileName: `CTO_${patient.firstName || ''}_${patient.lastName || ''}.pdf`,
          }
          if (remainingData?.User.email) {
            try {
              await sendJapaneseEmail({
                variables: {
                  inputData: {
                    email: remainingData?.User.email,
                    files: [pdfResult, pdfCTOResult],
                  },
                },
              })
              setToastMessage('Japanese forms sent', 'success')
            } catch {
              // do nothing
            }
          }
          return [pdfResult, pdfCTOResult]
        })
        handleConfirmModal()
      }

      setDownload(null)
    },
  })

  React.useEffect(() => {
    if (session.date) {
      setDate(parseISO(session.date))
    }
    if (session.endsAt) {
      setEndDate(parseISO(session.endsAt))
    }
  }, [session.date, session.endsAt])

  const addressForm = useAddressForm({
    onSubmit: () => {
      onChange({
        Location: addressForm.values as TestSessionDetailWithResult_testSession_Location,
        ...(endDate ? { endsAt: endDate } : {}),
      })
    },
    validationSchema: 'fullAddress',
  })
  React.useEffect(() => {
    void addressForm.setValues(session.Location as AddressFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(session.Location)])

  const [setTestSessionLab, { loading: isSetTestSessionLabLoading }] = useSetTestSessionLab()
  const [setAntigenTypeTestSession] = useSetAntigenTypeTestSession()

  const testTypeOptions = {
    Covid19: 'Covid19 RT-PCR',
    SofiaInfluenza: 'Influenza Antigen',
    SofiaStrep: 'Strep Antigen',
    Lucira: 'Lucira',
    SofiaRsv: 'RSV Antigen',
    BioSignFlu: 'BioSign® Flu A+B Test',
  }

  const showTestTypeText = (type: TestType[keyof TestType]) =>
    testTypeOptions[type as keyof typeof testTypeOptions] ?? 'Covid19 Rapid Molecular'

  const queryAllMembers = () => {
    void getAllMembers({
      variables: {
        id: session.id,
        skipResult: !isSuperAdminOrResultsOrLiaison,
        skipInsurance: false,
        skipMemberFields: false,
      },
    })
  }
  const downloadAllPatients = () => {
    setDownload('csvpatients')
    queryAllMembers()
  }

  const downloadJp = () => {
    setDownload('csv-jp')
    queryAllMembers()
  }

  const sendForms = () => {
    setDownload('send-japanese-email')
    queryAllMembers()
  }

  const downloadCSV = () => {
    setDownload('csvData')
    queryAllMembers()
  }

  const handleEventDefaultValues = (values: TestValuesInput) => {
    const {
      specimenType,
      rapidLotNumber,
      rapidCartridgeExpirationDate,
      rapidDockId,
      rapidSpecimenNumber,
      rapidExpirationDate,
    } = values
    onChange({
      TestValues: {
        __typename: 'EventTestValues',
        specimenType: specimenType || null,
        rapidLotNumber: rapidLotNumber || null,
        rapidCartridgeExpirationDate: rapidCartridgeExpirationDate || null,
        rapidDockId: rapidDockId || null,
        rapidSpecimenNumber: rapidSpecimenNumber || null,
        rapidExpirationDate: rapidExpirationDate || null,
      },
    })
  }

  const defaultValuesDialog = React.useMemo(
    () => (
      <DefaultValuesDialog
        testType={session?.type}
        isOpen={isDefaultValuesOpen}
        onDismiss={() => setIsDefaultValuesOpen(false)}
        onFinish={handleEventDefaultValues}
        testValues={session.TestValues ?? {}}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDefaultValuesOpen]
  )

  return (
    <>
      <Card>
        <Card.Title
          rightContent={
            <Box>
              <Box display="flex">
                <TestSessionStatusLabel session={session} />
                <TestSessionPaymentLabel
                  paymentStatus={session.paymentStatus}
                  invoices={session.Invoice?.invoiceDocNumber ?? session.Invoice?.invoiceId}
                  isInvoiceDocNumber={!!session.Invoice?.invoiceDocNumber}
                />
              </Box>
              {session.paymentError && (
                <TestSessionPaymentErrorLabel
                  paymentError={session.paymentError as TestSessionPaymentError}
                />
              )}
            </Box>
          }
        >
          Details
        </Card.Title>
        <Card.Content>
          {isMobileOrTablet && (
            <SessionName
              ml="0.25rem"
              mb="1rem"
              sessionType={session.type}
              initialSessionName={session.name}
              onChange={onChange}
              isEditLoading={isEditLoading}
              isEditMode={isEditMode}
              titleFontSize={fontSizes.l}
            />
          )}
          <Box display="grid" gridGap="0.25rem">
            <Box display="flex" alignItems="center">
              <Icon.Calendar flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
              <Text fontSize="m">
                {formattedDateTime}
                {formattedEndDateTime}
              </Text>
              {(isSuperAdmin || isScheduler || isCLS) && (
                <Button
                  ml="auto"
                  flex="none"
                  appearance="ghost"
                  disabled={!isEditMode}
                  onClick={() => {
                    setOpenedEditDialogType(EditDialogType.Date)
                  }}
                >
                  Change
                </Button>
              )}
            </Box>
            <Box display="flex" alignItems="center">
              <Icon.Pin flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
              <Text fontSize="m">{getFormattedAddress(session.Location)}</Text>
              {(isSuperAdmin || isScheduler || isCLS) && (
                <Button
                  ml="auto"
                  flex="none"
                  appearance="ghost"
                  disabled={!isEditMode}
                  onClick={() => {
                    setOpenedEditDialogType(EditDialogType.Location)
                  }}
                >
                  Change
                </Button>
              )}
            </Box>
            {!isEditMode && (
              <Box display="flex" alignItems="center">
                <Icon.DoneAllOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">{getFormattedDateAndTime(session.finishedAt, true)}</Text>
              </Box>
            )}
            {session.RequestedByUser && (
              <Box display="flex" alignItems="center">
                <Icon.Person flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Box mr="0.5rem">
                  <Text fontSize="m">Requested by: </Text>
                </Box>
                <Button
                  flex="none"
                  appearance="link"
                  onClick={() => {
                    history.push(`/${ROUTES.USERS}/${session.RequestedByUser!.id}`)
                  }}
                >
                  {session.RequestedByUser.firstName || ''} {session.RequestedByUser.lastName || ''}
                </Button>
              </Box>
            )}
            {session.estimatedMembers && (
              <Box display="flex" alignItems="center">
                <Icon.People flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Box mr="0.5rem">
                  <Text fontSize="m">Estimated members: {session.estimatedMembers}</Text>
                </Box>
              </Box>
            )}
            {session.type && (
              <Box display="flex" alignItems="center">
                <Icon.Heartbeat flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">Test type: </Text>
                <Box
                  mx="0.5rem"
                  flex={1}
                  flexDirection="row"
                  maxWidth="50%"
                  mb={getAntigenByCategory.length > 1 ? '16px' : null}
                >
                  {session.type && getAntigenByCategory.length > 1 ? (
                    <SelectInput
                      name="type"
                      value={session.type}
                      onChange={async (event) => {
                        try {
                          await setAntigenTypeTestSession({
                            variables: {
                              inputData: {
                                type: TestType[event.target.value as TestType],
                                testSessionId: session.id,
                              },
                            },
                          })
                          void refetchTestSessionData()
                          setToastMessage('Changes successfully saved', 'success')
                        } catch (e) {
                          // ignore error
                        }
                      }}
                      options={getAntigenByCategory}
                      hasBottomContent={false}
                      disabled={
                        !!session.Tests.length ||
                        !hasPermission([UserPermissions.SELECT_TEST_BRAND])
                      }
                    />
                  ) : (
                    <Text fontSize="m">{showTestTypeText(session.type)}</Text>
                  )}
                </Box>
              </Box>
            )}
            {(!!session.Groups?.length || session.Group) && (
              <Box display="flex">
                <Icon.People flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Box mr="0.5rem">
                  <Text fontSize="m">Group names: </Text>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  {session.Group && (
                    <Button
                      flex="none"
                      appearance="link"
                      onClick={() => {
                        history.push(
                          `/${ROUTES.COMPANIES}/${session.Company!.id}?groupId=${session.Group!.id}`
                        )
                      }}
                    >
                      {session.Group.name}
                    </Button>
                  )}

                  {!session.Group &&
                    session.Groups?.map(({ name, id }) => (
                      <React.Fragment key={id}>
                        <Button
                          flex="none"
                          appearance="link"
                          onClick={() => {
                            history.push(
                              `/${ROUTES.COMPANIES}/${session.Company!.id}?groupId=${id}`
                            )
                          }}
                        >
                          {name}
                        </Button>
                      </React.Fragment>
                    ))}
                </Box>
              </Box>
            )}

            {session.Company && (
              <Box display="flex" alignItems="center">
                <Icon.Briefcase flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Box mr="0.5rem">
                  <Text fontSize="m">Company name: </Text>
                </Box>
                <Button
                  flex="none"
                  appearance="link"
                  onClick={() => {
                    history.push(`/${ROUTES.COMPANIES}/${session.Company!.id}`)
                  }}
                >
                  {session.Company.name}
                </Button>
              </Box>
            )}
            {process.env.REACT_APP_SESSION_PHYSICIAN_NAME && (
              <Box display="flex" alignItems="center">
                <Icon.Heartbeat flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">
                  Physician name: {process.env.REACT_APP_SESSION_PHYSICIAN_NAME}
                </Text>
              </Box>
            )}
            {process.env.REACT_APP_SESSION_LAB_ADDRESS && (
              <Box display="flex" alignItems="center">
                <Icon.ThermometerOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">Lab address: {process.env.REACT_APP_SESSION_LAB_ADDRESS}</Text>
              </Box>
            )}
            <Loading isLoading={isSetTestSessionLabLoading} isEmpty={false}>
              <Box display="flex" alignItems="center">
                <Box flex="none" mr="0.5rem">
                  <Icon.ThermometerOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                </Box>
                <SelectInput
                  value={session.lab ?? NO_LAB_VALUE}
                  name="Lab"
                  disabled={
                    isSetTestSessionLabLoading || session.type === TestType.AntigenCareStart
                  }
                  options={LAB_OPTIONS.slice(1, 2)}
                  onChange={async (e) => {
                    try {
                      await setTestSessionLab({
                        variables: {
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          inputData: {
                            lab: Lab[e.target.value as Lab] || null,
                            testSessionId,
                          },
                        },
                      })
                      await refetchTestSessionData()
                      setToastMessage('', 'success')
                    } catch {
                      // ignore error
                    }
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center">
                <Icon.Home flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Sublocation
                  sublocation={session?.sublocation || ''}
                  sessionId={session?.id}
                  refetch={refetchTestSessionData}
                />
              </Box>
            </Loading>
            <Box pl="2.2rem" pb="1rem" flex="1">
              <Button
                onClick={() => {
                  setIsDefaultValuesOpen(true)
                }}
                colorVariant="primary"
              >
                Set default values
              </Button>
            </Box>
            {!!session.finishedAt &&
              hasPermission([UserPermissions.DOWNLOAD_RESULT_CSV_AFTER_FINISHED]) && (
                <Button
                  appearance="outlined"
                  colorVariant="info"
                  onClick={downloadCSV}
                  isFetching={TestSessionAllMembersLoading || download === 'csvData'}
                >
                  Download csv
                </Button>
              )}
            {!session.finishedAt &&
              hasPermission([UserPermissions.DOWNLOAD_USER_DATA_CSV_BEFORE_FINISHED]) && (
                <Button
                  appearance="outlined"
                  colorVariant="info"
                  onClick={downloadAllPatients}
                  isFetching={TestSessionAllMembersLoading && download === 'csvpatients'}
                >
                  Download list of patients
                </Button>
              )}
            {!!session.finishedAt &&
              hasPermission([UserPermissions.DOWNLOAD_ZIP_AFTER_FINISHED]) &&
              !!session.Tests.length &&
              session.Groups.some((item) => item.japaneseGroup) && (
                <>
                  <Button
                    appearance="outlined"
                    colorVariant="info"
                    onClick={downloadJp}
                    isFetching={TestSessionAllMembersLoading || download === 'csv-jp'}
                  >
                    Download zip
                  </Button>
                  <Button
                    appearance="outlined"
                    colorVariant="info"
                    onClick={handleConfirmModal}
                    isFetching={TestSessionAllMembersLoading || download === 'csv-jp'}
                  >
                    Send Japanese forms
                  </Button>
                </>
              )}

            <ConfirmationDialog
              title="Send Japanese Forms confirm"
              isOpen={isOpenConfirmModal}
              onDismiss={handleConfirmModal}
              actions={
                <>
                  <Button onClick={handleConfirmModal} colorVariant="secondary">
                    Cancel
                  </Button>
                  <Button
                    onClick={sendForms}
                    isFetching={TestSessionAllMembersLoading || download === 'csv-jp'}
                  >
                    Send Japanese forms
                  </Button>
                </>
              }
            >
              <Text>Are you sure you want to email the form?</Text>
            </ConfirmationDialog>
            {session.Products.length > 0 && (
              <OrderedProductsInfo
                priceOfAllProducts={session.totalPrice!}
                products={session.Products}
              />
            )}
          </Box>
        </Card.Content>
      </Card>
      {defaultValuesDialog}
      <Dialog
        maxWidth="400px"
        isOpen={!!openedEditDialogType}
        onDismiss={() => {
          setOpenedEditDialogType(null)
        }}
      >
        <Card.Title
          onDismiss={() => {
            setOpenedEditDialogType(null)
          }}
        />
        <Card.Content>
          {openedEditDialogType === 'Date' && (
            <>
              <DateInput
                id="date"
                name="date"
                label="Date"
                value={date}
                onChange={(date) => {
                  setDate(date)
                  setEndDate(null)
                }}
                isFullWidth
              />
              <DateInput
                id="time"
                name="time"
                label="Start Time"
                type="time"
                value={date}
                onChange={(date) => {
                  const currentDate = setTimeToDate(new Date(formattedDateTime || date), date)
                  setDate(!date ? date : currentDate)
                  setEndDate(null)
                }}
                isFullWidth
              />
              <DateInput
                id="endTime"
                name="endTime"
                label="End Time"
                type="time"
                value={endDate}
                onChange={(date) => {
                  setEndDate(date)
                }}
                minTime={
                  date
                    ? set(date, { hours: getHours(date), minutes: getMinutes(date) + 15 })
                    : undefined
                }
                maxTime={date ? set(date, { hours: 23, minutes: 59 }) : undefined}
                isFullWidth
              />
            </>
          )}
          {openedEditDialogType === 'Location' && (
            <AddressInput
              onChange={addressForm.setValues}
              onTouched={addressForm.setTouched}
              value={addressForm.values}
              errors={addressForm.errors}
              touched={addressForm.touched}
            />
          )}
        </Card.Content>
        <Card.Actions>
          <Button
            colorVariant="secondary"
            onClick={() => {
              setOpenedEditDialogType(null)
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={(session.endsAt && !endDate) || !date}
            isFetching={isEditing}
            colorVariant="success"
            onClick={() => {
              if (openedEditDialogType === EditDialogType.Location) {
                addressForm.handleSubmit()
              }
              if (openedEditDialogType === EditDialogType.Date) {
                onChange({
                  date: date ? formatISO(date) : undefined,
                  endsAt: endDate ? formatISO(endDate) : undefined,
                })
              }
            }}
          >
            Save
          </Button>
        </Card.Actions>
      </Dialog>
    </>
  )
}
export default SessionInfo
